import { Navigate, Outlet } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { iTokenDecoded } from "../../Services/Interfaces";
import { resetLang } from "../../i18n/lang";

/**
 * Encerra a sessão por completo.
 *
 * O idioma escolhido vive em duas pontas — o localStorage e uma variável de
 * módulo em i18n/lang — e limpar só uma delas dessincroniza a interface das
 * requisições: a tela segue no idioma antigo e o axios volta ao padrão. Por
 * isso as duas caem sempre juntas, aqui.
 */
const limparSessao = () => {
  localStorage.clear();
  resetLang();
};

export const Logout = (cancelMessage?: boolean) => {
  limparSessao();
  if (!cancelMessage) toast.info("Sessão encerrada com sucesso!");
  setTimeout(() => {
    if (window.location.pathname !== "/login") {
      return (window.location.href = "/login");
    } else {
      return;
    }
  }, 6000);
};

export const checkChaves = () => {
  if (localStorage.getItem("token") === "undefined") {
    limparSessao();
  }
  const token =
    localStorage.getItem("token") !== undefined &&
    localStorage.getItem("token") !== "" &&
    localStorage.getItem("token") !== null
      ? true
      : false;

  const tokenValido = () => {
    if (!token) return false;

    // jwtDecode estoura em token malformado. Sem o try, a exceção subia
    // durante o render da guarda e derrubava a árvore inteira — tela branca,
    // não tela de login.
    try {
      const decoded = jwtDecode(
        localStorage.getItem("token") as string
      ) as any;

      if (decoded.exp < Date.now() / 1000) {
        limparSessao();
        return false;
      }

      return true;
    } catch {
      // token ilegível é sessão morta: limpar aqui é correto, e o usuário
      // entra de novo
      limparSessao();
      return false;
    }
  };

  return token && tokenValido();
};

export const checkPerfilAdmin = () => {
  const token =
    localStorage.getItem("token") !== undefined &&
    localStorage.getItem("token") !== "" &&
    localStorage.getItem("token") !== null
      ? true
      : false;

  if (token) {
    const decoded = jwtDecode(
      localStorage.getItem("token") as string
    ) as iTokenDecoded;

    return (
      decoded.roles.includes("ROLE_ADMIN") ||
      decoded.roles.includes("ROLE_SUPER_ADMIN")
    );
  }
  return false;
};

export const checkPerfilEstablishment = () => {
  const token =
    localStorage.getItem("token") !== undefined &&
    localStorage.getItem("token") !== "" &&
    localStorage.getItem("token") !== null
      ? true
      : false;

  if (token) {
    const decoded = jwtDecode(
      localStorage.getItem("token") as string
    ) as iTokenDecoded;

    return decoded.roles.includes("ROLE_USER_ESTABLISHMENT");
  }
  return false;
};

/**
 * Para onde a sessão atual pertence, ou null quando não há sessão utilizável
 * neste painel.
 *
 * Um token válido sem ROLE_ADMIN nem ROLE_USER_ESTABLISHMENT (um usuário comum
 * do app mobile, por exemplo) devolve null: ele não tem para onde ir aqui, e é
 * o Login que avisa isso, com mensagem.
 */
export const rotaInicialDaSessao = (): string | null => {
  if (!checkChaves()) return null;
  if (checkPerfilAdmin()) return "/home";
  if (checkPerfilEstablishment()) return "/home_establishment";
  return null;
};

// ─── Guards ────────────────────────────────────────────────────────────────────
//
// Guarda redireciona, nunca encerra sessão.
//
// Antes, estas duas funções chamavam limparSessao() e Logout(true) durante o
// render. Qualquer re-render da guarda em uma rota que não casasse com o perfil
// apagava o localStorage — e era isso que transformava um "voltar" do navegador
// em logout de verdade, sem o usuário ter pedido nada. Encerrar sessão é ação
// do usuário (botão Sair) ou consequência de token inválido, que o checkChaves
// já trata.

/** Rotas exclusivas de admin */
export const AdminRoutes = () => {
  if (!checkChaves()) return <Navigate to="/login" replace />;
  if (checkPerfilAdmin()) return <Outlet />;
  if (checkPerfilEstablishment()) return <Navigate to="/home_establishment" replace />;
  // autenticado, mas sem perfil para este painel: o Login mostra o motivo
  return <Navigate to="/login" replace />;
};

/** Rotas exclusivas de usuário estabelecimento */
export const EstablishmentRoutes = () => {
  if (!checkChaves()) return <Navigate to="/login" replace />;
  if (checkPerfilEstablishment()) return <Outlet />;
  if (checkPerfilAdmin()) return <Navigate to="/home" replace />;
  return <Navigate to="/login" replace />;
};